/**
 * Topo Nomad — "Frame Your Trek" live poster editor (Phase 1, client-only)
 * ------------------------------------------------------------------------
 * Plain vanilla JS, no dependencies, no build step. Everything lives under
 * the single global `TopoNomadEditor` namespace; all DOM it creates is
 * scoped under the `.tn-editor` class (see tn-editor.css).
 *
 * Rendering is a PURE function: (template config + design state) -> canvas
 * pixels. The same Design JSON always produces the same poster, which is
 * what makes the Phase 2 server-side print render (300 DPI) possible from
 * the same data. Nothing is stored in the DOM; state lives in one object.
 *
 * Public API:
 *   TopoNomadEditor.registerTemplate(tpl)   — called by template assets
 *   TopoNomadEditor.init(config)            — mount the editor
 *   TopoNomadEditor.getDesign()             — current Design JSON (object)
 *   TopoNomadEditor.load(jsonOrString)      — restore a Design JSON
 *   TopoNomadEditor.validateDesign(json)    — {ok, errors[]}
 *
 * Design JSON schema: see README → "Design JSON".
 */
(function () {
  'use strict';

  var TN = (window.TopoNomadEditor = window.TopoNomadEditor || {});
  TN._templates = TN._templates || {};
  TN.registerTemplate =
    TN.registerTemplate ||
    function (tpl) {
      TN._templates[tpl.id] = tpl;
    };

  var APP_VERSION = '1.0.0';
  var SCHEMA_VERSION = '1.0';
  var HISTORY_LIMIT = 60;

  /* ======================================================================
   * Utilities
   * ==================================================================== */

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  /** Tiny hyperscript: el('div', {class:'x', onclick:fn}, [children|string]) */
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v == null) return;
        if (k === 'text') node.textContent = v;
        else if (k === 'html') node.innerHTML = v; // trusted, static markup only
        else if (k.indexOf('on') === 0) node.addEventListener(k.slice(2), v);
        else node.setAttribute(k, v);
      });
    }
    (children || []).forEach(function (c) {
      if (c == null) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  var ICONS = {
    chevron: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M3 6l5 5 5-5"/></svg>',
    undo: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3L2.5 6.5 6 10"/><path d="M2.5 6.5H10a3.5 3.5 0 010 7H7"/></svg>',
    redo: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3l3.5 3.5L10 10"/><path d="M13.5 6.5H6a3.5 3.5 0 000 7h3"/></svg>',
    reset: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M13.5 8a5.5 5.5 0 11-1.6-3.9M13.5 2.5v3h-3"/></svg>',
    photo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M3 17l5-4 4 3 4-4 5 5"/></svg>',
    close: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M3 3l10 10M13 3L3 13"/></svg>',
    pen: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11.5 2.5l2 2L5 13l-2.8.8L3 11z"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z"/></svg>',
    expand: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 2.5h4v4M6.5 13.5h-4v-4M13.5 2.5L9 7M2.5 13.5L7 9"/></svg>'
  };

  /* ======================================================================
   * Text measurement & fitting (deterministic across browsers: letter
   * tracking is applied manually, character by character)
   * ==================================================================== */

  function fontString(template, fontKey, weight, px) {
    var f = template.fonts[fontKey] || template.fonts.value;
    return (weight || f.weight || 400) + ' ' + px + 'px ' + f.family;
  }

  function measureTracked(ctx, text, trackingPx) {
    if (!trackingPx) return ctx.measureText(text).width;
    var w = 0;
    for (var i = 0; i < text.length; i++) w += ctx.measureText(text[i]).width;
    return w + trackingPx * Math.max(0, text.length - 1);
  }

  function drawTracked(ctx, text, x, y, align, trackingPx) {
    if (!trackingPx) {
      ctx.textAlign = align;
      ctx.fillText(text, x, y);
      return;
    }
    var total = measureTracked(ctx, text, trackingPx);
    var cx = x;
    if (align === 'center') cx = x - total / 2;
    else if (align === 'right') cx = x - total;
    ctx.textAlign = 'left';
    for (var i = 0; i < text.length; i++) {
      ctx.fillText(text[i], cx, y);
      cx += ctx.measureText(text[i]).width + trackingPx;
    }
  }

  /** Greedy word wrap. Force-breaks words longer than the box. */
  function wrapText(ctx, text, maxW, trackingPx) {
    var words = text.split(/\s+/).filter(Boolean);
    var lines = [];
    var line = '';
    words.forEach(function (word) {
      var probe = line ? line + ' ' + word : word;
      if (measureTracked(ctx, probe, trackingPx) <= maxW || !line) {
        line = probe;
      } else {
        lines.push(line);
        line = word;
      }
    });
    if (line) lines.push(line);
    return lines;
  }

  /**
   * Fit a field's text into its box: try each shrink step (e.g. 100% →
   * 85% → 70%), wrapping up to max_lines; overflow is never allowed.
   * Returns { px, lines[], trackingPx } — deterministic for given inputs.
   */
  function fitFieldText(ctx, template, field, text, S) {
    var steps = field.shrink || [1];
    var maxLines = field.max_lines || 1;
    var boxW = field.box.w * S;
    var result = null;

    for (var i = 0; i < steps.length; i++) {
      var px = field.size * steps[i] * S;
      ctx.font = fontString(template, field.font, field.weight, px);
      var trackingPx = (field.tracking || 0) * px;
      var lines = maxLines > 1 ? wrapText(ctx, text, boxW, trackingPx) : [text];
      var fits = lines.length <= maxLines;
      if (fits) {
        for (var j = 0; j < lines.length; j++) {
          if (measureTracked(ctx, lines[j], trackingPx) > boxW) { fits = false; break; }
        }
      }
      result = { px: px, lines: lines, trackingPx: trackingPx };
      if (fits) return result;
    }

    // Smallest step still overflows: clip to max lines, ellipsize each line.
    result.lines = result.lines.slice(0, maxLines).map(function (line) {
      while (line.length > 1 && measureTracked(ctx, line + '…', result.trackingPx) > boxW) {
        line = line.slice(0, -1);
      }
      return measureTracked(ctx, line, result.trackingPx) > boxW ? line + '…' : line;
    });
    return result;
  }

  function applyTransform(text, transform) {
    return transform === 'upper' ? text.toUpperCase() : text;
  }

  /* ======================================================================
   * Poster renderer — the heart of the product. Pure: no state reads
   * beyond its arguments, so Phase 2 can reuse it for print rendering.
   * ==================================================================== */

  function drawStarPath(ctx, cx, cy, rOuter) {
    var rInner = rOuter * 0.44;
    ctx.beginPath();
    for (var i = 0; i < 10; i++) {
      var r = i % 2 === 0 ? rOuter : rInner;
      var a = -Math.PI / 2 + (i * Math.PI) / 5;
      var x = cx + r * Math.cos(a);
      var y = cy + r * Math.sin(a);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();
  }

  function drawSun(ctx, x, y, sizePx) {
    var r = sizePx / 2;
    ctx.beginPath();
    ctx.arc(x, y, r * 0.5, 0, Math.PI * 2);
    ctx.lineWidth = Math.max(1, sizePx * 0.09);
    ctx.stroke();
    for (var i = 0; i < 8; i++) {
      var a = (i * Math.PI) / 4;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * r * 0.72, y + Math.sin(a) * r * 0.72);
      ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
      ctx.stroke();
    }
  }

  /** Cloud + rain + peeking sun, line-art. x,y = icon center (mm*S px). */
  function drawWeatherIcon(ctx, x, y, sizePx) {
    var s = sizePx;
    var lw = Math.max(1, s * 0.045);
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // sun, top-right
    var sx = x + s * 0.26, sy = y - s * 0.3, sr = s * 0.13;
    ctx.beginPath();
    ctx.arc(sx, sy, sr, -0.6, Math.PI * 1.25);
    ctx.stroke();
    for (var i = 0; i < 5; i++) {
      var a = -Math.PI / 2 + (i - 2) * 0.55;
      ctx.beginPath();
      ctx.moveTo(sx + Math.cos(a) * sr * 1.4, sy + Math.sin(a) * sr * 1.4);
      ctx.lineTo(sx + Math.cos(a) * sr * 1.9, sy + Math.sin(a) * sr * 1.9);
      ctx.stroke();
    }

    // cloud
    ctx.beginPath();
    ctx.moveTo(x - s * 0.32, y + s * 0.1);
    ctx.arc(x - s * 0.22, y - s * 0.02, s * 0.15, Math.PI * 0.9, Math.PI * 1.55);
    ctx.arc(x + 0, y - s * 0.14, s * 0.19, Math.PI * 1.05, Math.PI * 1.9);
    ctx.arc(x + s * 0.2, y - s * 0.02, s * 0.14, Math.PI * 1.25, Math.PI * 0.5);
    ctx.lineTo(x - s * 0.32, y + s * 0.1);
    ctx.stroke();

    // rain
    for (var r = -1; r <= 1; r++) {
      ctx.beginPath();
      ctx.moveTo(x + r * s * 0.16 + s * 0.03, y + s * 0.2);
      ctx.lineTo(x + r * s * 0.16 - s * 0.03, y + s * 0.36);
      ctx.stroke();
    }
  }

  /** Vertical TOPO NOMAD mark: small map icon above rotated wordmark.
   * elDef: { x (center of column, mm), y (bottom of wordmark), size, icon_y } */
  function drawVerticalLogo(ctx, template, elDef, S) {
    var x = elDef.x * S;
    var px = (elDef.size || 3.1) * S;

    // folded-map icon
    var iy = (elDef.icon_y || elDef.y - 17) * S;
    var half = 2.2 * S;
    ctx.lineWidth = Math.max(1, 0.35 * S);
    ctx.lineJoin = 'round';
    ctx.strokeRect(x - half, iy - half, half * 2, half * 2);
    ctx.beginPath();
    ctx.moveTo(x - half * 0.34, iy - half);
    ctx.lineTo(x - half * 0.34, iy + half);
    ctx.moveTo(x + half * 0.34, iy - half);
    ctx.lineTo(x + half * 0.34, iy + half);
    ctx.stroke();

    // rotated wordmark, reading bottom-to-top
    ctx.save();
    ctx.translate(x, elDef.y * S);
    ctx.rotate(-Math.PI / 2);
    ctx.font = fontString(template, 'label', 600, px);
    ctx.textBaseline = 'middle';
    drawTracked(ctx, 'TOPO NOMAD', 0, 0, 'left', 0.3 * px);
    ctx.restore();
  }

  /**
   * Render the poster into `canvas`.
   * env: { photoImage, photoNatural:{w,h}, routeImage, watermark:bool }
   */
  function renderPoster(canvas, template, state, env) {
    var ctx = canvas.getContext('2d');
    var W = canvas.width;
    var H = canvas.height;
    var S = W / template.artboard.width; // px per mm
    var ink = template.ink || '#FFFFFF';

    ctx.save();
    ctx.clearRect(0, 0, W, H);

    /* -- 1. Photo layer (or placeholder) ------------------------------- */
    if (env.skipBackground) {
      // Tuner/alignment mode: artwork only, transparent background.
    } else if (env.photoImage) {
      var img = env.photoImage;
      var iw = img.width, ih = img.height;
      var s0 = Math.max(W / iw, H / ih);
      var s = s0 * state.photo.crop.zoom;
      var vw = W / s, vh = H / s;
      var cx = clamp(state.photo.crop.cx, vw / (2 * iw), 1 - vw / (2 * iw));
      var cy = clamp(state.photo.crop.cy, vh / (2 * ih), 1 - vh / (2 * ih));
      ctx.drawImage(img, cx * iw - vw / 2, cy * ih - vh / 2, vw, vh, 0, 0, W, H);
    } else {
      var bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#31536f');
      bg.addColorStop(0.55, '#1d3a51');
      bg.addColorStop(1, '#0e2233');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      // faint placeholder contours
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 1.2;
      for (var c = 0; c < 5; c++) {
        ctx.beginPath();
        ctx.ellipse(W * 0.5, H * 0.55, W * (0.18 + c * 0.13), H * (0.1 + c * 0.08), -0.2, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    /* -- 2. Overlay layer ---------------------------------------------- */
    if (!env.skipBackground) {
      var tint = null;
      for (var t = 0; t < template.overlay.tints.length; t++) {
        if (template.overlay.tints[t].id === state.overlay.tint) tint = template.overlay.tints[t];
      }
      tint = tint || template.overlay.tints[0];
      var op = clamp(state.overlay.opacity, 0, template.overlay.max_opacity);
      ctx.save();
      ctx.globalAlpha = op;
      ctx.fillStyle = tint.color;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
      if (state.overlay.gradient) {
        var g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, tint.color);
        g.addColorStop(0.24, 'rgba(0,0,0,0)');
        g.addColorStop(0.62, 'rgba(0,0,0,0)');
        g.addColorStop(1, tint.color);
        ctx.save();
        ctx.globalAlpha = Math.min(0.55, 0.25 + op * 0.6);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }
    }

    /* -- 3. Route layer ------------------------------------------------ */
    var rm = template.route_map;
    if (rm && rm.mode === 'placeholder') {
      // Editor-preview-only placeholder frame where the designers' route
      // artwork will sit. Never part of the final render.
      if (env.interactive && env.routePlaceholder !== false) {
        var pb = rm.box;
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.lineWidth = Math.max(1, 0.35 * S);
        ctx.setLineDash([2.4 * S, 1.8 * S]);
        ctx.strokeRect(pb.x * S, pb.y * S, pb.w * S, pb.h * S);
        ctx.setLineDash([]);
        var pcx = (pb.x + pb.w / 2) * S;
        var pcy = (pb.y + pb.h / 2) * S;
        var p1 = 4.6 * S;
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.font = fontString(template, 'label', 600, p1);
        ctx.textBaseline = 'alphabetic';
        drawTracked(ctx, 'YOUR ROUTE MAP', pcx, pcy - 1 * S, 'center', 0.18 * p1);
        var p2 = 3 * S;
        ctx.fillStyle = 'rgba(255,255,255,0.38)';
        ctx.font = fontString(template, 'value', 400, p2);
        drawTracked(ctx, 'hand-drawn by our designers after checkout', pcx, pcy + 4 * S, 'center', 0.04 * p2);
        ctx.restore();
      }
    } else if (env.routeImage && env.routeImage.complete && env.routeImage.naturalWidth) {
      var rb = rm.box;
      var bx = rb.x * S, by = rb.y * S, bw = rb.w * S, bh = rb.h * S;
      var ar = env.routeImage.naturalWidth / env.routeImage.naturalHeight;
      var dw = bw, dh = bw / ar;
      if (dh > bh) { dh = bh; dw = bh * ar; }
      ctx.save();
      ctx.globalAlpha = rm.opacity != null ? rm.opacity : 1;
      ctx.drawImage(env.routeImage, bx + (bw - dw) / 2, by + (bh - dh) / 2, dw, dh);
      ctx.restore();
    }

    /* -- Photo hint chip (only while empty) ----------------------------- */
    if (!env.photoImage && env.interactive && !env.skipBackground) {
      var hint = 'TAP TO ADD YOUR TRIP PHOTO';
      var hp = 3.4 * S;
      ctx.font = fontString(template, 'label', 600, hp);
      ctx.textBaseline = 'alphabetic';
      var hw = measureTracked(ctx, hint, 0.12 * hp) + 8 * S;
      var hx = W / 2, hy = (template.route_map.box.y + template.route_map.box.h - 12) * S;
      ctx.save();
      ctx.fillStyle = 'rgba(255,255,255,0.14)';
      ctx.beginPath();
      var rH = 7 * S;
      ctx.roundRect ? ctx.roundRect(hx - hw / 2, hy - rH / 2, hw, rH, rH / 2)
                    : ctx.rect(hx - hw / 2, hy - rH / 2, hw, rH);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      drawTracked(ctx, hint, hx, hy + hp * 0.34, 'center', 0.12 * hp);
      ctx.restore();
    }

    /* -- 4. Text/data layer -------------------------------------------- */
    ctx.fillStyle = ink;
    ctx.textBaseline = 'alphabetic';
    template.fields.forEach(function (f) {
      if (f.no_render || f.widget) return;
      var raw;
      if (f.compose) {
        var parts = f.compose_from.map(function (id) { return (state.fields[id] || '').trim(); });
        if (!parts.some(Boolean)) return;
        raw = f.compose.replace(/\{(\d)\}/g, function (_, n) { return parts[+n]; });
      } else {
        raw = state.fields[f.id] || '';
      }
      var text = applyTransform(raw, f.transform).trim();
      if (!text) return;

      var fit = fitFieldText(ctx, template, f, text, S);
      var lineH = fit.px * (f.line_height || 1.15);
      var totalH = fit.px + lineH * (fit.lines.length - 1);
      var baseY = f.box.y * S + (f.box.h * S - totalH) / 2 + fit.px * 0.78;
      var anchorX =
        f.align === 'center' ? (f.box.x + f.box.w / 2) * S :
        f.align === 'right' ? (f.box.x + f.box.w) * S :
        f.box.x * S;

      ctx.font = fontString(template, f.font, f.weight, fit.px);
      ctx.fillStyle = f.color || ink;
      fit.lines.forEach(function (line, i) {
        drawTracked(ctx, line, anchorX, baseY + i * lineH, f.align, fit.trackingPx);
      });
    });

    /* -- 5. Fixed brand elements ---------------------------------------- */
    (template.fixed || []).forEach(function (fx) {
      ctx.save();
      ctx.globalAlpha = fx.alpha != null ? fx.alpha : 1;
      ctx.strokeStyle = ink;
      ctx.fillStyle = ink;
      switch (fx.type) {
        case 'line':
          ctx.lineWidth = (fx.w || 0.4) * S;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(fx.x1 * S, fx.y1 * S);
          ctx.lineTo(fx.x2 * S, fx.y2 * S);
          ctx.stroke();
          break;
        case 'dot':
          ctx.beginPath();
          ctx.arc(fx.x * S, fx.y * S, (fx.r || 0.6) * S, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 'text': {
          var px = fx.size * S;
          ctx.font = fontString(template, fx.font || 'label', fx.weight, px);
          ctx.textBaseline = 'alphabetic';
          drawTracked(ctx, fx.text, fx.x * S, (fx.y + fx.size) * S, fx.align || 'left', (fx.tracking || 0) * px);
          break;
        }
        case 'stars': {
          var rating = clamp(Math.round(state.fields[fx.field] || 0), 0, 5);
          var size = fx.size * S, gap = fx.gap * S;
          var total = 5 * size + 4 * gap;
          var x0 = fx.cx * S - total / 2 + size / 2;
          var cy2 = fx.y * S + size / 2;
          for (var i = 0; i < 5; i++) {
            drawStarPath(ctx, x0 + i * (size + gap), cy2, size / 2);
            if (i < rating) {
              ctx.fill();
            } else {
              ctx.save();
              ctx.globalAlpha *= 0.55;
              ctx.lineWidth = Math.max(1, 0.35 * S);
              ctx.stroke();
              ctx.restore();
            }
          }
          break;
        }
        case 'icon':
          if (fx.icon === 'sun') drawSun(ctx, fx.x * S, fx.y * S, fx.size * S);
          else if (fx.icon === 'weather') drawWeatherIcon(ctx, fx.x * S, fx.y * S, fx.size * S);
          break;
        case 'logo-vertical':
          drawVerticalLogo(ctx, template, fx, S);
          break;
      }
      ctx.restore();
    });

    /* -- Focused-field highlight (editor preview only) ------------------ */
    if (env.highlight && env.interactive) {
      var hb = env.highlight;
      var hpad = 1.4 * S;
      ctx.save();
      ctx.strokeStyle = 'rgba(242, 92, 5, 0.95)';
      ctx.lineWidth = Math.max(1.5, 0.4 * S);
      ctx.setLineDash([1.8 * S, 1.3 * S]);
      ctx.beginPath();
      var hx2 = hb.x * S - hpad, hy2 = hb.y * S - hpad;
      var hw2 = hb.w * S + hpad * 2, hh2 = hb.h * S + hpad * 2;
      if (ctx.roundRect) ctx.roundRect(hx2, hy2, hw2, hh2, 1.5 * S);
      else ctx.rect(hx2, hy2, hw2, hh2);
      ctx.stroke();
      ctx.restore();
    }

    /* -- 6. Preview watermark ------------------------------------------- */
    if (env.watermark) {
      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.rotate(-Math.PI / 6);
      var wmPx = 7 * S;
      ctx.font = fontString(template, 'label', 600, wmPx);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      var text = 'PREVIEW — toponomad.co';
      for (var row = -3; row <= 3; row++) {
        ctx.fillStyle = 'rgba(0,0,0,0.05)';
        ctx.fillText(text, row % 2 === 0 ? 0 : W * 0.35, row * 24 * S + 1.5);
        ctx.fillStyle = 'rgba(255,255,255,0.13)';
        ctx.fillText(text, row % 2 === 0 ? 0 : W * 0.35, row * 24 * S);
      }
      ctx.restore();
    }

    ctx.restore();
  }
  TN.renderPoster = renderPoster; // exposed for Phase 2 admin/print reuse

  /* ======================================================================
   * Design JSON validation (small hand-rolled validator — no deps)
   * ==================================================================== */

  function validateDesign(design) {
    var errors = [];
    function err(msg) { errors.push(msg); }
    if (!design || typeof design !== 'object') return { ok: false, errors: ['Design is not an object'] };
    if (design.schema_version !== SCHEMA_VERSION) err('Unsupported schema_version: ' + design.schema_version);
    var tpl = TN._templates[design.template_id];
    if (!tpl) err('Unknown template_id: ' + design.template_id);
    if (!design.fields || typeof design.fields !== 'object') err('Missing fields object');

    if (tpl && design.fields) {
      tpl.fields.forEach(function (f) {
        if (f.compose) return;
        var v = design.fields[f.id];
        if (f.widget === 'stars') {
          if (v != null && (typeof v !== 'number' || v < 0 || v > 5)) err('fields.' + f.id + ' must be 0–5');
        } else if (v != null) {
          if (typeof v !== 'string') err('fields.' + f.id + ' must be a string');
          else if (f.max_chars && v.length > f.max_chars) err('fields.' + f.id + ' exceeds ' + f.max_chars + ' chars');
        }
      });
    }
    if (design.overlay) {
      var o = design.overlay;
      if (typeof o.opacity !== 'number' || o.opacity < 0 || o.opacity > 0.7) err('overlay.opacity must be 0–0.7');
      if (tpl && !tpl.overlay.tints.some(function (t) { return t.id === o.tint; })) err('Unknown overlay.tint: ' + o.tint);
    } else err('Missing overlay object');
    if (design.photo) {
      var cr = design.photo.crop;
      if (!cr || typeof cr.cx !== 'number' || typeof cr.cy !== 'number' || typeof cr.zoom !== 'number') {
        err('photo.crop must have numeric cx, cy, zoom');
      } else if (cr.zoom < 1 || cr.zoom > 3) err('photo.crop.zoom must be 1–3');
    }
    return { ok: errors.length === 0, errors: errors };
  }
  TN.validateDesign = validateDesign;

  /* ======================================================================
   * Editor instance
   * ==================================================================== */

  function Editor(root, config) {
    var self = this;
    this.root = root;
    this.config = config;
    this.template = TN._templates[config.templateId];
    if (!this.template) {
      root.textContent = 'Poster editor: unknown template "' + config.templateId + '".';
      return;
    }
    this.watermark = config.watermark !== false;

    /* ---- state ---- */
    this.state = this.defaultState();
    this.photoImage = null;          // downscaled preview bitmap (render)
    this.routeImage = null;
    this.createdAt = null;
    this.inputs = {};                // field id -> input element
    this.past = [];
    this.future = [];
    this.present = JSON.stringify(this.state);

    this.buildDom();
    this.bindCanvas();
    this.loadRouteMap();
    this.sizeCanvas();
    this.scheduleRender();
    this.emit('tn:editor-opened', { template: this.template.id });

    // Re-render once webfonts arrive so text metrics are correct.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.scheduleRender(); });
    }
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(function () { self.sizeCanvas(); self.scheduleRender(); });
      this._ro.observe(this.stageFrame);
    }
    window.addEventListener('resize', function () { self.sizeCanvas(); self.scheduleRender(); });
    // Mobile keyboards resize the visual viewport, not the window.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function () {
        self.sizeCanvas();
        self.scheduleRender();
      });
    }
  }

  Editor.prototype.defaultState = function () {
    var fields = {};
    this.template.fields.forEach(function (f) {
      if (f.compose) return;
      fields[f.id] = f.default != null ? f.default : (f.widget === 'stars' ? 5 : '');
    });
    return {
      fields: fields,
      photo: null, // {filename, original_width, original_height, crop:{cx,cy,zoom}}
      overlay: {
        opacity: this.template.overlay.default_opacity,
        tint: this.template.overlay.default_tint,
        gradient: !!this.template.overlay.default_gradient
      },
      route_notes: ''
    };
  };

  Editor.prototype.emit = function (name, detail) {
    this.root.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true }));
  };

  /* ---------------------------- DOM ---------------------------------- */

  Editor.prototype.buildDom = function () {
    var self = this;
    var tpl = this.template;
    this.root.classList.add('tn-editor');

    /* stage */
    this.canvas = el('canvas', { 'aria-label': 'Live poster preview' });
    this.zoomBtn = el('button', {
      class: 'tn-zoom-btn',
      type: 'button',
      'aria-label': 'Enlarge preview',
      html: ICONS.expand,
      onclick: function () { self.openPreview(); }
    });
    this.stageWrap = el('div', { class: 'tn-stage__canvas-wrap' }, [this.canvas, this.zoomBtn]);
    this.stageFrame = el('div', { class: 'tn-stage__frame' }, [this.stageWrap]);
    var stage = el('div', { class: 'tn-stage' }, [
      this.stageFrame,
      el('p', { class: 'tn-stage__note' }, [
        el('strong', { text: 'Live preview. ' }),
        'Drag the photo to reposition, scroll or pinch to zoom, tap any text to edit it. ' +
        'The watermark disappears on your printed poster. ' + tpl.route_map.note
      ])
    ]);
    this.stageEl = stage;

    /* panel */
    var groupsWrap = document.createDocumentFragment();
    tpl.groups.forEach(function (g, gi) {
      groupsWrap.appendChild(self.buildGroup(g, gi));
    });

    this.undoBtn = el('button', { class: 'tn-btn-ghost', type: 'button', html: ICONS.undo + 'Undo', onclick: function () { self.undo(); } });
    this.redoBtn = el('button', { class: 'tn-btn-ghost', type: 'button', html: ICONS.redo + 'Redo', onclick: function () { self.redo(); } });
    var resetBtn = el('button', { class: 'tn-btn-ghost', type: 'button', html: ICONS.reset + 'Reset', onclick: function () { self.resetToDefaults(); } });

    /* Mobile step wizard chrome (hidden on desktop via CSS) */
    this.stepLabel = el('span');
    this.stepPct = el('em');
    this.stepFill = el('div', { class: 'tn-stepnav__fill' });
    var stepNav = el('div', { class: 'tn-stepnav' }, [
      el('div', { class: 'tn-stepnav__label' }, [this.stepLabel, this.stepPct]),
      el('div', { class: 'tn-stepnav__track' }, [this.stepFill])
    ]);
    this.prevBtn = el('button', { class: 'tn-btn-ghost', type: 'button', text: 'Back', onclick: function () { self.setStep(self.step - 1, true); } });
    this.fullBtn = el('button', { class: 'tn-btn-ghost', type: 'button', text: 'Full preview', onclick: function () { self.openPreview(); } });
    this.nextBtn = el('button', { class: 'tn-btn-primary', type: 'button', text: 'Next', onclick: function () {
      if (self.step >= self.groupEls.length - 1) self.save();
      else self.setStep(self.step + 1, true);
    } });
    var stepFoot = el('div', { class: 'tn-stepfoot' }, [this.prevBtn, this.fullBtn, this.nextBtn]);

    var panel = el('div', { class: 'tn-panel' }, [
      el('div', { class: 'tn-panel__header' }, [
        el('p', { class: 'tn-panel__kicker', text: 'Frame your trek' }),
        el('h2', { class: 'tn-panel__title', text: tpl.name }),
        el('p', { class: 'tn-panel__sub', text: 'Fill in your trip. Watch the poster come alive as you type.' })
      ]),
      el('div', { class: 'tn-toolbar' }, [this.undoBtn, this.redoBtn, el('span', { class: 'tn-toolbar__spacer' }), resetBtn]),
      stepNav,
      groupsWrap,
      stepFoot,
      el('div', { class: 'tn-cta' }, [
        el('button', { class: 'tn-btn-primary', type: 'button', text: 'Bring your trip to life', onclick: function () { self.save(); } }),
        el('p', { class: 'tn-cta__hint', text: 'The preview watermark never appears on your printed poster.' })
      ])
    ]);

    this.layout = el('div', { class: 'tn-editor__layout' }, [panel, stage]);
    this.root.appendChild(this.layout);
    this.buildModal();
    this.buildPreviewModal();
    this.updateUndoButtons();

    this.groupEls = Array.prototype.slice.call(this.root.querySelectorAll('details.tn-group'));
    this.step = 0;
    this._mq = window.matchMedia('(max-width: 899px)');
    var onMode = function () { self.applyMode(); };
    if (this._mq.addEventListener) this._mq.addEventListener('change', onMode);
    else this._mq.addListener(onMode);
    this.applyMode();
  };

  /** Desktop: all sections open at once. Mobile: StillNovel-style wizard —
   * one section at a time, preview pinned, Back/Next + progress bar. */
  Editor.prototype.applyMode = function () {
    var mobile = this._mq.matches;
    this.root.classList.toggle('tn-stepper', mobile);
    if (mobile) {
      this.setStep(this.step, false);
    } else {
      this.groupEls.forEach(function (g) { g.open = true; g.classList.remove('tn-step-on'); });
    }
  };

  Editor.prototype.setStep = function (i, scroll) {
    var n = this.groupEls.length;
    this.step = clamp(i, 0, n - 1);
    var self = this;
    this.groupEls.forEach(function (g, idx) {
      g.open = true;
      g.classList.toggle('tn-step-on', idx === self.step);
    });
    var groupLabel = (this.template.groups[this.step] || {}).label || '';
    this.stepLabel.textContent = 'Step ' + (this.step + 1) + ' of ' + n + ' — ' + groupLabel;
    this.stepPct.textContent = Math.round(((this.step + 1) / n) * 100) + '%';
    this.stepFill.style.width = (((this.step + 1) / n) * 100) + '%';
    this.prevBtn.disabled = this.step === 0;
    var last = this.step === n - 1;
    this.nextBtn.textContent = last ? 'Bring your trip to life' : 'Next';
    this.fullBtn.style.display = last ? '' : 'none';
    if (scroll) {
      var g = this.groupEls[this.step];
      g.style.scrollMarginTop = (this.stageEl.offsetHeight + 6) + 'px';
      g.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  Editor.prototype.buildGroup = function (group, index) {
    var self = this;
    var body = el('div', { class: 'tn-group__body' }, [
      group.hint ? el('p', { class: 'tn-group__hint', text: group.hint }) : null
    ]);

    if (group.id === 'photo') {
      this.buildPhotoGroup(body);
    } else {
      var pendingRow = null;
      this.template.fields.forEach(function (f) {
        if (f.compose || f.group !== group.id) return;
        if (f.widget === 'stars') { body.appendChild(self.buildStarField(f)); return; }
        var fieldEl = self.buildTextField(f);
        if (f.row) {
          if (!pendingRow || pendingRow._row !== f.row) {
            pendingRow = el('div', { class: 'tn-row' });
            pendingRow._row = f.row;
            body.appendChild(pendingRow);
          }
          pendingRow.appendChild(fieldEl);
        } else {
          pendingRow = null;
          body.appendChild(fieldEl);
        }
      });
      if (group.id === 'memories') this.buildRouteNotes(body);
    }

    return el('details', { class: 'tn-group' }, [
      el('summary', {}, [
        el('span', { class: 'tn-group__num', text: '0' + (index + 1) }),
        el('span', { class: 'tn-group__label', text: group.label }),
        el('span', { class: 'tn-group__chev', html: ICONS.chevron })
      ]),
      body
    ]);
  };

  Editor.prototype.buildTextField = function (f) {
    var self = this;
    var count = el('span', { class: 'tn-field__count', text: (this.state.fields[f.id] || '').length + ' / ' + f.max_chars });
    var input = el('input', {
      type: 'text',
      maxlength: f.max_chars,
      value: this.state.fields[f.id] || '',
      placeholder: f.default || '',
      'aria-label': f.label,
      onfocus: function () {
        self.focusedField = f.id;
        self.scheduleRender();
      },
      onblur: function () {
        if (self.focusedField === f.id) {
          self.focusedField = null;
          self.scheduleRender();
        }
      },
      oninput: function () {
        self.state.fields[f.id] = input.value;
        count.textContent = input.value.length + ' / ' + f.max_chars;
        count.classList.toggle('tn-at-limit', input.value.length >= f.max_chars);
        self.scheduleRender();
        self.commitHistoryDebounced();
        self.emit('tn:field-edited', { field: f.id });
        if (f.id === 'destination') self.checkSuggest();
      }
    });
    this.inputs[f.id] = input;

    var kids = [
      el('div', { class: 'tn-field__top' }, [
        el('span', { class: 'tn-field__label', text: f.label }),
        count
      ]),
      input
    ];
    if (f.help) kids.push(el('div', { class: 'tn-field__help', text: f.help }));

    if (f.id === 'destination' && this.template.suggest) {
      this.suggestText = el('span');
      this.suggestBtn = el('button', { type: 'button', text: 'Use these', onclick: function () { self.applySuggest(); } });
      this.suggestChip = el('div', { class: 'tn-suggest' }, [this.suggestText, this.suggestBtn]);
      kids.push(this.suggestChip);
    }
    return el('div', { class: 'tn-field', 'data-field': f.id }, kids);
  };

  Editor.prototype.buildStarField = function (f) {
    var self = this;
    this.starButtons = [];
    var wrap = el('div', { class: 'tn-stars', role: 'radiogroup', 'aria-label': f.label });
    for (var i = 1; i <= 5; i++) {
      (function (n) {
        var b = el('button', { type: 'button', html: ICONS.star, 'aria-label': n + ' star' + (n > 1 ? 's' : ''), onclick: function () {
          self.state.fields[f.id] = n;
          self.updateStarWidget();
          self.scheduleRender();
          self.commitHistory();
          self.emit('tn:field-edited', { field: f.id });
        } });
        self.starButtons.push(b);
        wrap.appendChild(b);
      })(i);
    }
    this.updateStarWidget();
    return el('div', { class: 'tn-field', 'data-field': f.id }, [
      el('div', { class: 'tn-field__top' }, [el('span', { class: 'tn-field__label', text: f.label })]),
      wrap
    ]);
  };

  Editor.prototype.updateStarWidget = function () {
    var rating = this.state.fields.rating || 0;
    (this.starButtons || []).forEach(function (b, i) {
      b.classList.toggle('tn-on', i < rating);
    });
  };

  Editor.prototype.buildRouteNotes = function (body) {
    var self = this;
    var count = el('span', { class: 'tn-field__count', text: '0 / 280' });
    this.routeNotesInput = el('textarea', {
      maxlength: 280,
      placeholder: 'e.g. Started in Manali → Baralacha La → Leh → Khardung La → Pangong → back via Srinagar',
      'aria-label': 'Your exact route',
      oninput: function () {
        self.state.route_notes = self.routeNotesInput.value;
        count.textContent = self.routeNotesInput.value.length + ' / 280';
        self.commitHistoryDebounced();
      }
    });
    body.appendChild(el('div', { class: 'tn-field', 'data-field': 'route_notes' }, [
      el('div', { class: 'tn-field__top' }, [
        el('span', { class: 'tn-field__label', text: 'Your exact route' }),
        count
      ]),
      this.routeNotesInput,
      el('div', { class: 'tn-designer-note' }, [
        el('span', { html: ICONS.pen }),
        el('span', { text: this.template.route_map.note + ' Tell us the route here — start, passes, stops, end.' })
      ])
    ]));
  };

  Editor.prototype.buildPhotoGroup = function (body) {
    var self = this;
    var tplOverlay = this.template.overlay;

    /* upload */
    this.fileInput = el('input', { type: 'file', accept: 'image/jpeg,image/png,image/heic,image/heif' });
    this.fileInput.addEventListener('change', function () {
      if (self.fileInput.files && self.fileInput.files[0]) self.handleFile(self.fileInput.files[0]);
    });
    var upload = el('div', { class: 'tn-upload', role: 'button', tabindex: '0', 'aria-label': 'Upload your trip photo' }, [
      el('div', { class: 'tn-upload__icon', html: ICONS.photo }),
      el('p', { class: 'tn-upload__title', text: 'Add your trip photo' }),
      el('p', { class: 'tn-upload__hint', text: 'JPG, PNG or HEIC · at least 1800 × 2400 px for a crisp print' }),
      this.fileInput
    ]);
    upload.addEventListener('click', function () { self.fileInput.click(); });
    upload.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.fileInput.click(); } });
    upload.addEventListener('dragover', function (e) { e.preventDefault(); upload.classList.add('tn-dragover'); });
    upload.addEventListener('dragleave', function () { upload.classList.remove('tn-dragover'); });
    upload.addEventListener('drop', function (e) {
      e.preventDefault();
      upload.classList.remove('tn-dragover');
      if (e.dataTransfer.files && e.dataTransfer.files[0]) self.handleFile(e.dataTransfer.files[0]);
    });
    body.appendChild(upload);

    this.qualityBadge = el('div', { class: 'tn-quality', role: 'status' });
    body.appendChild(this.qualityBadge);

    this.photoFileRow = el('div', { class: 'tn-photo-file' });
    body.appendChild(this.photoFileRow);

    /* photo zoom */
    this.zoomSlider = el('input', { type: 'range', min: '100', max: '300', step: '1', value: '100', 'aria-label': 'Photo zoom' });
    this.zoomValue = el('span', { class: 'tn-slider__value', text: '100%' });
    this.zoomSlider.addEventListener('input', function () {
      if (!self.state.photo) return;
      self.state.photo.crop.zoom = +self.zoomSlider.value / 100;
      self.zoomValue.textContent = self.zoomSlider.value + '%';
      self.updateQualityBadge();
      self.scheduleRender();
    });
    this.zoomSlider.addEventListener('change', function () { self.commitHistory(); });
    body.appendChild(el('div', { class: 'tn-slider' }, [
      el('div', { class: 'tn-slider__top' }, [
        el('span', { class: 'tn-slider__label', text: 'Photo zoom' }),
        this.zoomValue
      ]),
      this.zoomSlider,
      el('div', { class: 'tn-field__help', text: 'Drag the poster preview to reposition your photo.' })
    ]));

    /* overlay opacity */
    var opMax = Math.round(tplOverlay.max_opacity * 100);
    this.opacitySlider = el('input', { type: 'range', min: '0', max: String(opMax), step: '1', value: String(Math.round(this.state.overlay.opacity * 100)), 'aria-label': 'Overlay strength' });
    this.opacityValue = el('span', { class: 'tn-slider__value', text: Math.round(this.state.overlay.opacity * 100) + '%' });
    this.opacitySlider.addEventListener('input', function () {
      self.state.overlay.opacity = +self.opacitySlider.value / 100;
      self.opacityValue.textContent = self.opacitySlider.value + '%';
      self.scheduleRender();
    });
    this.opacitySlider.addEventListener('change', function () { self.commitHistory(); });
    body.appendChild(el('div', { class: 'tn-slider' }, [
      el('div', { class: 'tn-slider__top' }, [
        el('span', { class: 'tn-slider__label', text: 'Overlay strength' }),
        this.opacityValue
      ]),
      this.opacitySlider,
      el('div', { class: 'tn-field__help', text: 'Darkens your photo just enough to keep the map and text readable.' })
    ]));

    /* tints */
    this.swatches = {};
    var sw = el('div', { class: 'tn-swatches', role: 'radiogroup', 'aria-label': 'Overlay tint' });
    tplOverlay.tints.forEach(function (t) {
      var b = el('button', { type: 'button', class: 'tn-swatch', style: 'background:' + t.color, title: t.label, 'aria-label': t.label, onclick: function () {
        self.state.overlay.tint = t.id;
        self.updateSwatches();
        self.scheduleRender();
        self.commitHistory();
      } });
      self.swatches[t.id] = b;
      sw.appendChild(b);
    });
    body.appendChild(el('div', { class: 'tn-slider' }, [
      el('div', { class: 'tn-slider__top' }, [el('span', { class: 'tn-slider__label', text: 'Overlay tint' })]),
      sw
    ]));
    this.updateSwatches();

    /* gradient toggle */
    this.gradientBtn = el('button', { type: 'button', 'aria-pressed': String(!!this.state.overlay.gradient), onclick: function () {
      self.state.overlay.gradient = !self.state.overlay.gradient;
      self.gradientBtn.setAttribute('aria-pressed', String(self.state.overlay.gradient));
      self.scheduleRender();
      self.commitHistory();
    } });
    body.appendChild(el('div', { class: 'tn-toggle' }, [
      el('span', { class: 'tn-toggle__label', text: 'Top & bottom gradient' }),
      this.gradientBtn
    ]));
  };

  Editor.prototype.updateSwatches = function () {
    var self = this;
    Object.keys(this.swatches || {}).forEach(function (id) {
      self.swatches[id].classList.toggle('tn-on', id === self.state.overlay.tint);
    });
  };

  /* ------------------------- suggestions ------------------------------ */

  Editor.prototype.checkSuggest = function () {
    if (!this.suggestChip) return;
    var key = (this.state.fields.destination || '').trim().toUpperCase();
    var hit = this.template.suggest && this.template.suggest[key];
    if (hit && (this.state.fields.altitude !== hit.altitude || this.state.fields.coordinates !== hit.coordinates)) {
      this._suggestHit = hit;
      this.suggestText.textContent = key + ': ' + hit.altitude + ' · ' + hit.coordinates;
      this.suggestChip.classList.add('tn-show');
    } else {
      this.suggestChip.classList.remove('tn-show');
    }
  };

  Editor.prototype.applySuggest = function () {
    if (!this._suggestHit) return;
    this.state.fields.altitude = this._suggestHit.altitude;
    this.state.fields.coordinates = this._suggestHit.coordinates;
    this.syncInputs();
    this.suggestChip.classList.remove('tn-show');
    this.scheduleRender();
    this.commitHistory();
  };

  /* ---------------------------- photo --------------------------------- */

  Editor.prototype.handleFile = function (file) {
    var self = this;
    var isImage = /^image\//.test(file.type) || /\.(heic|heif)$/i.test(file.name);
    if (!isImage) {
      this.setQuality('red', 'That doesn’t look like a photo. Please choose a JPG, PNG or HEIC file.');
      return;
    }
    this.decodeImage(file).then(function (img) {
      var ow = img.width, oh = img.height;
      var block = self.template.print.block_below_px;
      // Portrait poster: compare against the smaller/larger dimension so
      // landscape photos aren't unfairly rejected.
      var shortSide = Math.min(ow, oh), longSide = Math.max(ow, oh);
      if (shortSide < block[0] || longSide < block[1]) {
        self.setQuality('red', 'This photo is ' + ow + ' × ' + oh + ' px — too small to print sharply at 30 × 40 cm. Please pick one at least 1200 × 1600 px (bigger is better).');
        return;
      }
      self.photoImage = self.downscale(img, 2400);
      self.state.photo = {
        source: 'local',
        filename: file.name,
        original_width: ow,
        original_height: oh,
        crop: { cx: 0.5, cy: 0.5, zoom: 1 }
      };
      self.zoomSlider.value = '100';
      self.zoomValue.textContent = '100%';
      self.updateQualityBadge();
      self.updatePhotoFileRow();
      self.scheduleRender();
      self.commitHistory();
      self.emit('tn:photo-uploaded', { width: ow, height: oh });
    }).catch(function () {
      self.setQuality('red', 'We couldn’t read that file. HEIC support varies by browser — try exporting it as JPG.');
    });
  };

  Editor.prototype.decodeImage = function (file) {
    return new Promise(function (resolve, reject) {
      if (window.createImageBitmap) {
        createImageBitmap(file).then(resolve, function () { fallback(); });
      } else fallback();
      function fallback() {
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
        img.src = url;
      }
    });
  };

  /** Downscale to a preview bitmap for smooth canvas rendering. The
   * original file is untouched (it will be uploaded as-is in Phase 2). */
  Editor.prototype.downscale = function (img, maxDim) {
    var w = img.width, h = img.height;
    var k = Math.min(1, maxDim / Math.max(w, h));
    if (k === 1) return img;
    var c = document.createElement('canvas');
    c.width = Math.round(w * k);
    c.height = Math.round(h * k);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return c;
  };

  Editor.prototype.removePhoto = function () {
    this.photoImage = null;
    this.state.photo = null;
    this.qualityBadge.classList.remove('tn-show');
    this.updatePhotoFileRow();
    this.scheduleRender();
    this.commitHistory();
  };

  Editor.prototype.updatePhotoFileRow = function () {
    var self = this;
    this.photoFileRow.innerHTML = '';
    if (!this.state.photo) {
      this.photoFileRow.classList.remove('tn-show');
      return;
    }
    var label = this.state.photo.filename + (this.photoImage ? '' : ' — please re-upload this photo');
    this.photoFileRow.appendChild(el('span', { text: label }));
    this.photoFileRow.appendChild(el('button', { type: 'button', text: 'Remove', onclick: function () { self.removePhoto(); } }));
    this.photoFileRow.classList.add('tn-show');
  };

  Editor.prototype.setQuality = function (level, message) {
    this.qualityBadge.className = 'tn-quality tn-show tn-quality--' + level;
    this.qualityBadge.innerHTML = '';
    this.qualityBadge.appendChild(el('span', { class: 'tn-quality__dot' }));
    this.qualityBadge.appendChild(el('span', { text: message }));
  };

  /** Effective resolution accounts for zoom: zooming in crops the photo,
   * so fewer source pixels cover the same 30 × 40 cm of paper. */
  Editor.prototype.updateQualityBadge = function () {
    var p = this.state.photo;
    if (!p) return;
    var ratio = this.template.artboard.width / this.template.artboard.height; // 0.75
    var effW = Math.min(p.original_width, p.original_height * ratio) / p.crop.zoom;
    var effH = effW / ratio;
    var warn = this.template.print.warn_below_px;
    var block = this.template.print.block_below_px;
    if (effW >= warn[0] && effH >= warn[1]) {
      this.setQuality('green', 'Great quality — this will print beautifully.');
    } else if (effW >= block[0] && effH >= block[1]) {
      this.setQuality('amber', 'Decent quality. Fine details may soften a little in print — zooming out or a larger photo would be even crisper.');
    } else {
      this.setQuality('red', 'Zoomed in this far, the photo will look soft in print. Try zooming out or choosing a larger photo.');
    }
  };

  /* -------------------------- canvas ---------------------------------- */

  Editor.prototype.sizeCanvas = function () {
    var mobile = window.matchMedia('(max-width: 899px)').matches;
    var wrapW = this.stageWrap.clientWidth || this.stageFrame.clientWidth;
    var cssW = wrapW;
    if (mobile) {
      // Fit the preview to the screen at the correct 3:4 ratio, sized
      // against a *stable* viewport height: while an editor input is
      // focused (on-screen keyboard likely open) or a dialog is showing,
      // ignore viewport shrinks so the preview never jumps smaller.
      // It only re-sizes on real changes (rotation, window resize).
      var h = window.innerHeight;
      var w = window.innerWidth;
      var ae = document.activeElement;
      var hold =
        (ae && this.root.contains(ae) && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) ||
        (this.previewModal && this.previewModal.open) ||
        (this.modal && this.modal.open);
      if (this._vpW !== w) { this._vpW = w; this._stableH = 0; } // rotated / resized
      this._stableH = hold ? Math.max(this._stableH || 0, h) : h;
      var ratio = 0.75; // artboard w/h
      cssW = Math.min(wrapW, Math.round(this._stableH * 0.42 * ratio));
    }
    cssW = Math.max(140, cssW);
    var cssH = Math.round((cssW * 4) / 3);
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var pxW = Math.min(Math.round(cssW * dpr), 1500);
    var pxH = Math.round((pxW * 4) / 3);
    if (this.canvas.width !== pxW || this.canvas.height !== pxH) {
      this.canvas.width = pxW;
      this.canvas.height = pxH;
    }
    this.canvas.style.width = cssW + 'px';
    this.canvas.style.height = cssH + 'px';
    // The canvas is centered inside the wrap; keep the enlarge button on
    // the canvas corner, not the wrap corner.
    if (this.zoomBtn) {
      var off = Math.max(0, Math.round((wrapW - cssW) / 2)) + 8;
      this.zoomBtn.style.right = off + 'px';
    }
  };

  Editor.prototype.loadRouteMap = function () {
    var self = this;
    var key = this.template.route_map && this.template.route_map.asset_key;
    if (!key) return; // placeholder mode — nothing to load
    var url = (this.config.assets || {})[key] || key;
    var img = new Image();
    img.onload = function () { self.routeImage = img; self.scheduleRender(); };
    img.src = url;
  };

  Editor.prototype.scheduleRender = function () {
    var self = this;
    if (this._raf) return;
    this._raf = requestAnimationFrame(function () {
      self._raf = null;
      var highlight = null;
      if (self.focusedField) {
        self.template.fields.forEach(function (f) {
          if (f.box && (f.id === self.focusedField ||
              (f.compose && f.compose_from.indexOf(self.focusedField) !== -1))) {
            highlight = f.box;
          }
        });
      }
      renderPoster(self.canvas, self.template, self.state, {
        photoImage: self.photoImage,
        routeImage: self.routeImage,
        watermark: self.watermark,
        routePlaceholder: self.config.routePlaceholder !== false,
        highlight: highlight,
        interactive: true
      });
      if (self.previewModal && self.previewModal.open) self.renderZoom();
    });
  };

  /* Canvas gestures: drag = pan photo, wheel/pinch = zoom photo,
   * tap = focus the matching input (or open the photo picker). */
  Editor.prototype.bindCanvas = function () {
    var self = this;
    var canvas = this.canvas;
    var pointers = {};
    var moved = 0;
    var lastMid = null, lastDist = null;

    function canvasScale() { return canvas.width / canvas.clientWidth; }

    function panBy(dxCss, dyCss) {
      var p = self.state.photo;
      if (!p || !self.photoImage) return;
      var k = canvasScale();
      var W = canvas.width, H = canvas.height;
      var iw = self.photoImage.width, ih = self.photoImage.height;
      var s = Math.max(W / iw, H / ih) * p.crop.zoom;
      p.crop.cx = clamp(p.crop.cx - (dxCss * k) / (s * iw), 0, 1);
      p.crop.cy = clamp(p.crop.cy - (dyCss * k) / (s * ih), 0, 1);
      self.scheduleRender();
    }

    function zoomBy(factor) {
      var p = self.state.photo;
      if (!p) return;
      p.crop.zoom = clamp(p.crop.zoom * factor, 1, 3);
      self.zoomSlider.value = String(Math.round(p.crop.zoom * 100));
      self.zoomValue.textContent = Math.round(p.crop.zoom * 100) + '%';
      self.updateQualityBadge();
      self.scheduleRender();
    }

    canvas.addEventListener('pointerdown', function (e) {
      canvas.setPointerCapture(e.pointerId);
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      moved = 0;
      lastMid = null; lastDist = null;
      if (Object.keys(pointers).length === 1 && self.photoImage) canvas.classList.add('tn-grabbing');
    });

    canvas.addEventListener('pointermove', function (e) {
      var ids = Object.keys(pointers);
      if (!pointers[e.pointerId]) {
        // hover cursor feedback
        if (ids.length === 0) {
          var hitField = self.hitTest(e);
          canvas.classList.toggle('tn-can-tap', !!hitField);
          canvas.classList.toggle('tn-can-grab', !hitField && !!self.photoImage);
        }
        return;
      }
      var prev = pointers[e.pointerId];
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      ids = Object.keys(pointers);

      if (ids.length === 1) {
        var dx = e.clientX - prev.x, dy = e.clientY - prev.y;
        moved += Math.abs(dx) + Math.abs(dy);
        panBy(dx, dy);
      } else if (ids.length === 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        var mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        var dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (lastDist) {
          moved += 10;
          zoomBy(dist / lastDist);
          panBy(mid.x - lastMid.x, mid.y - lastMid.y);
        }
        lastMid = mid; lastDist = dist;
      }
    });

    function endPointer(e) {
      if (!pointers[e.pointerId]) return;
      delete pointers[e.pointerId];
      canvas.classList.remove('tn-grabbing');
      lastMid = null; lastDist = null;
      if (Object.keys(pointers).length === 0) {
        if (moved < 8) self.handleTap(e);
        else if (self.state.photo) self.commitHistory();
      }
    }
    canvas.addEventListener('pointerup', endPointer);
    canvas.addEventListener('pointercancel', endPointer);

    canvas.addEventListener('wheel', function (e) {
      if (!self.state.photo) return;
      e.preventDefault();
      zoomBy(Math.exp(-e.deltaY * 0.0015));
      self.commitHistoryDebounced();
    }, { passive: false });
  };

  /** Map a pointer event to the template field whose box contains it. */
  Editor.prototype.hitTest = function (e) {
    var rect = this.canvas.getBoundingClientRect();
    var mmX = ((e.clientX - rect.left) / rect.width) * this.template.artboard.width;
    var mmY = ((e.clientY - rect.top) / rect.height) * this.template.artboard.height;
    var hit = null;
    this.template.fields.forEach(function (f) {
      if (!f.box) return;
      var b = f.box;
      if (mmX >= b.x && mmX <= b.x + b.w && mmY >= b.y - 1 && mmY <= b.y + b.h + 1) hit = f;
    });
    return hit;
  };

  Editor.prototype.handleTap = function (e) {
    var f = this.hitTest(e);
    if (!f) {
      if (!this.state.photo) this.fileInput.click();
      return;
    }
    var targetId = f.compose ? f.compose_from[0] : f.id;
    var input = this.inputs[targetId];
    if (!input) return;
    var fieldEl = input.closest('.tn-field');
    var group = input.closest('details');
    if (group) {
      group.open = true;
      // In wizard mode, jump to the step that owns this field.
      if (this.root.classList.contains('tn-stepper')) {
        var idx = this.groupEls.indexOf(group);
        if (idx !== -1 && idx !== this.step) this.setStep(idx, false);
      }
    }
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
    if (fieldEl) {
      fieldEl.classList.add('tn-flash');
      setTimeout(function () { fieldEl.classList.remove('tn-flash'); }, 1200);
    }
  };

  /* ------------------------- history ----------------------------------- */

  Editor.prototype.serialize = function () {
    return JSON.stringify(this.state);
  };

  Editor.prototype.commitHistory = function () {
    var snap = this.serialize();
    if (snap === this.present) return;
    this.past.push(this.present);
    if (this.past.length > HISTORY_LIMIT) this.past.shift();
    this.present = snap;
    this.future = [];
    this.updateUndoButtons();
  };

  Editor.prototype.applySnapshot = function (snap) {
    this.state = JSON.parse(snap);
    if (!this.state.photo) this.photoImage = null;
    this.syncInputs();
    this.scheduleRender();
  };

  Editor.prototype.undo = function () {
    if (!this.past.length) return;
    this.future.push(this.present);
    this.present = this.past.pop();
    this.applySnapshot(this.present);
    this.updateUndoButtons();
  };

  Editor.prototype.redo = function () {
    if (!this.future.length) return;
    this.past.push(this.present);
    this.present = this.future.pop();
    this.applySnapshot(this.present);
    this.updateUndoButtons();
  };

  Editor.prototype.updateUndoButtons = function () {
    this.undoBtn.disabled = this.past.length === 0;
    this.redoBtn.disabled = this.future.length === 0;
  };

  Editor.prototype.resetToDefaults = function () {
    if (!window.confirm('Reset everything to the template defaults? Your photo and all text will be cleared.')) return;
    this.state = this.defaultState();
    this.photoImage = null;
    this.qualityBadge.classList.remove('tn-show');
    this.syncInputs();
    this.scheduleRender();
    this.commitHistory();
  };

  /** Push current state into every panel control (after undo/load/reset). */
  Editor.prototype.syncInputs = function () {
    var self = this;
    Object.keys(this.inputs).forEach(function (id) {
      var input = self.inputs[id];
      var v = self.state.fields[id] != null ? String(self.state.fields[id]) : '';
      if (input.value !== v) input.value = v;
      var count = input.closest('.tn-field') && input.closest('.tn-field').querySelector('.tn-field__count');
      if (count) {
        var max = input.getAttribute('maxlength');
        count.textContent = v.length + ' / ' + max;
        count.classList.toggle('tn-at-limit', v.length >= +max);
      }
    });
    this.updateStarWidget();
    this.updateSwatches();
    if (this.routeNotesInput) this.routeNotesInput.value = this.state.route_notes || '';
    this.opacitySlider.value = String(Math.round(this.state.overlay.opacity * 100));
    this.opacityValue.textContent = Math.round(this.state.overlay.opacity * 100) + '%';
    this.gradientBtn.setAttribute('aria-pressed', String(!!this.state.overlay.gradient));
    var zoom = this.state.photo ? Math.round(this.state.photo.crop.zoom * 100) : 100;
    this.zoomSlider.value = String(zoom);
    this.zoomValue.textContent = zoom + '%';
    this.updatePhotoFileRow();
    if (this.state.photo && this.photoImage) this.updateQualityBadge();
    else if (!this.state.photo) this.qualityBadge.classList.remove('tn-show');
    this.checkSuggest();
  };

  /* ------------------------ save / load -------------------------------- */

  Editor.prototype.buildDesign = function () {
    var now = new Date().toISOString();
    if (!this.createdAt) this.createdAt = now;
    return {
      schema_version: SCHEMA_VERSION,
      template_id: this.template.id,
      created_at: this.createdAt,
      updated_at: now,
      fields: JSON.parse(JSON.stringify(this.state.fields)),
      photo: this.state.photo ? JSON.parse(JSON.stringify(this.state.photo)) : null,
      overlay: JSON.parse(JSON.stringify(this.state.overlay)),
      route_notes: this.state.route_notes || '',
      editor: { app_version: APP_VERSION }
    };
  };

  Editor.prototype.loadDesign = function (design) {
    var check = validateDesign(design);
    if (!check.ok) return check;
    if (design.template_id !== this.template.id) {
      return { ok: false, errors: ['This design uses template "' + design.template_id + '", but the editor is showing "' + this.template.id + '".'] };
    }
    var fresh = this.defaultState();
    Object.keys(fresh.fields).forEach(function (id) {
      if (design.fields[id] != null) fresh.fields[id] = design.fields[id];
    });
    fresh.overlay = {
      opacity: clamp(design.overlay.opacity, 0, this.template.overlay.max_opacity),
      tint: design.overlay.tint,
      gradient: !!design.overlay.gradient
    };
    fresh.route_notes = design.route_notes || '';
    if (design.photo) {
      fresh.photo = {
        source: design.photo.source || 'local',
        filename: design.photo.filename || 'photo',
        original_width: design.photo.original_width,
        original_height: design.photo.original_height,
        crop: {
          cx: clamp(design.photo.crop.cx, 0, 1),
          cy: clamp(design.photo.crop.cy, 0, 1),
          zoom: clamp(design.photo.crop.zoom, 1, 3)
        }
      };
    }
    this.state = fresh;
    this.createdAt = design.created_at || null;
    // Phase 1 stores no photo bytes in the JSON — prompt a re-upload.
    if (!fresh.photo) this.photoImage = null;
    else if (!this.photoImage) this.updatePhotoFileRow();
    this.syncInputs();
    this.scheduleRender();
    this.commitHistory();
    return { ok: true, errors: [] };
  };

  Editor.prototype.save = function () {
    var design = this.buildDesign();
    var json = JSON.stringify(design, null, 2);
    /* Phase 2: POST json + original photo file to the API here. */
    console.log('[TopoNomadEditor] Design JSON:', design);
    this.emit('tn:design-submitted', { design: design });
    this.openModal(json, design);
  };

  Editor.prototype.downloadJson = function (json, design) {
    var blob = new Blob([json], { type: 'application/json' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'toponomad-design-' + design.template_id + '-' + design.updated_at.replace(/[:.]/g, '-') + '.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(function () { URL.revokeObjectURL(a.href); }, 4000);
  };

  /* ---------------------------- modal ---------------------------------- */

  Editor.prototype.buildModal = function () {
    var self = this;
    this.modalPre = el('pre');
    this.loadError = el('span', { class: 'tn-load-error' });
    this.loadTextarea = el('textarea', { placeholder: 'Paste a Design JSON here…' });

    var loadFile = el('input', { type: 'file', accept: 'application/json', style: 'display:none' });
    loadFile.addEventListener('change', function () {
      var f = loadFile.files && loadFile.files[0];
      if (!f) return;
      f.text().then(function (t) { self.loadTextarea.value = t; self.tryLoadFromModal(); });
    });

    this.modal = el('dialog', { class: 'tn-modal' }, [
      el('div', { class: 'tn-modal__inner' }, [
        el('button', { class: 'tn-modal__close', type: 'button', 'aria-label': 'Close', html: ICONS.close, onclick: function () { self.modal.close(); } }),
        el('div', { class: 'tn-modal__head' }, [
          el('h3', { text: 'Your design is ready' }),
          el('p', { text: 'This is the Design JSON our team uses to produce your print file. In the live store this is saved automatically — for now you can download or copy it.' })
        ]),
        this.modalPre,
        el('div', { class: 'tn-modal__actions' }, [
          el('button', { class: 'tn-btn-primary', type: 'button', text: 'Download .json', onclick: function () { self.downloadJson(self._modalJson, self._modalDesign); } }),
          el('button', { class: 'tn-btn-ghost', type: 'button', text: 'Copy to clipboard', onclick: function (e) {
            navigator.clipboard.writeText(self._modalJson).then(function () {
              e.target.textContent = 'Copied ✓';
              setTimeout(function () { e.target.textContent = 'Copy to clipboard'; }, 1600);
            });
          } })
        ]),
        el('details', { class: 'tn-modal__load' }, [
          el('summary', { text: 'Load a saved design (round-trip test)' }),
          this.loadTextarea,
          el('div', { class: 'tn-load-actions' }, [
            el('button', { class: 'tn-btn-ghost', type: 'button', text: 'Load JSON', onclick: function () { self.tryLoadFromModal(); } }),
            el('button', { class: 'tn-btn-ghost', type: 'button', text: 'From file…', onclick: function () { loadFile.click(); } }),
            this.loadError
          ]),
          loadFile
        ])
      ])
    ]);
    this.root.appendChild(this.modal);
  };

  /* ---------------- enlarged full-screen preview ----------------------- */

  Editor.prototype.buildPreviewModal = function () {
    var self = this;
    this.zoomCanvas = el('canvas', { 'aria-label': 'Enlarged poster preview' });
    this.previewModal = el('dialog', { class: 'tn-preview' }, [
      el('div', { class: 'tn-preview__inner' }, [
        this.zoomCanvas,
        el('button', { class: 'tn-preview__close', type: 'button', 'aria-label': 'Close preview', html: ICONS.close, onclick: function () { self.previewModal.close(); } }),
        el('p', { class: 'tn-preview__hint', text: 'Tap anywhere to close · watermark disappears in print' })
      ])
    ]);
    // Tap outside the poster (or on the hint) closes it.
    this.previewModal.addEventListener('click', function (e) {
      if (e.target !== self.zoomCanvas) self.previewModal.close();
    });
    this.root.appendChild(this.previewModal);
  };

  Editor.prototype.openPreview = function () {
    if (typeof this.previewModal.showModal === 'function') this.previewModal.showModal();
    else this.previewModal.setAttribute('open', '');
    this.renderZoom();
    this.emit('tn:preview-enlarged', {});
  };

  /** Render the poster as large and sharp as the screen allows. */
  Editor.prototype.renderZoom = function () {
    var vpH = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
    var maxW = window.innerWidth - 24;
    var maxH = vpH - 76; // room for the close button + hint
    var ratio = this.template.artboard.width / this.template.artboard.height;
    var cssW = Math.min(maxW, Math.round(maxH * ratio));
    var cssH = Math.round(cssW / ratio);
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    var pxW = Math.min(Math.round(cssW * dpr), 2400);
    var pxH = Math.round(pxW / ratio);
    if (this.zoomCanvas.width !== pxW || this.zoomCanvas.height !== pxH) {
      this.zoomCanvas.width = pxW;
      this.zoomCanvas.height = pxH;
    }
    this.zoomCanvas.style.width = cssW + 'px';
    this.zoomCanvas.style.height = cssH + 'px';
    renderPoster(this.zoomCanvas, this.template, this.state, {
      photoImage: this.photoImage,
      routeImage: this.routeImage,
      watermark: this.watermark,
      routePlaceholder: this.config.routePlaceholder !== false,
      interactive: true
    });
  };

  Editor.prototype.tryLoadFromModal = function () {
    var res = TN.load(this.loadTextarea.value);
    if (res.ok) {
      this.loadError.textContent = '';
      this.modal.close();
    } else {
      this.loadError.textContent = res.errors[0] || 'Could not load that JSON.';
    }
  };

  Editor.prototype.openModal = function (json, design) {
    this._modalJson = json;
    this._modalDesign = design;
    this.modalPre.textContent = json;
    if (typeof this.modal.showModal === 'function') this.modal.showModal();
    else this.modal.setAttribute('open', '');
  };

  /* ======================================================================
   * Bootstrapping & public API
   * ==================================================================== */

  TN.init = function (config) {
    var root = typeof config.mount === 'string' ? document.getElementById(config.mount) : config.mount;
    if (!root) {
      console.error('[TopoNomadEditor] mount element not found');
      return null;
    }
    var editor = new Editor(root, config);
    TN._instance = editor;

    editor.commitHistoryDebounced = debounce(function () { editor.commitHistory(); }, 500);

    // Undo/redo keyboard shortcuts, only while focus is inside the editor.
    root.addEventListener('keydown', function (e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      var k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); editor.undo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); editor.redo(); }
    });
    return editor;
  };

  TN.getDesign = function () {
    return TN._instance ? TN._instance.buildDesign() : null;
  };

  TN.load = function (jsonOrString) {
    if (!TN._instance) return { ok: false, errors: ['Editor not initialised'] };
    var design = jsonOrString;
    if (typeof jsonOrString === 'string') {
      try { design = JSON.parse(jsonOrString); }
      catch (e) { return { ok: false, errors: ['Invalid JSON: ' + e.message] }; }
    }
    return TN._instance.loadDesign(design);
  };

  /* Auto-boot: any .tn-editor-mount div with an embedded JSON config. */
  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.tn-editor-mount'), function (mount) {
      if (mount._tnBooted) return;
      mount._tnBooted = true;
      var cfg = { mount: mount, templateId: mount.getAttribute('data-template') || 'ladakh', assets: {} };
      var cfgScript = mount.querySelector('script[type="application/json"]');
      if (cfgScript) {
        try {
          var parsed = JSON.parse(cfgScript.textContent);
          Object.keys(parsed).forEach(function (k) { cfg[k] = parsed[k]; });
        } catch (e) {
          console.error('[TopoNomadEditor] bad embedded config', e);
        }
      }
      TN.init(cfg);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
